import React from 'react';

import { DateFormat, formatDate } from '@lyearn/core/utils/date/formatDate';
import { Calendar2Outline, Close } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import {
  BaseButton,
  Box,
  DatePickerContainerProps,
  DateVariant,
  LinkButton,
  SelectedTime,
  Tooltip,
  Typography,
} from '@lyearn/ui';
import DatePicker from '@/components/DatePicker';
import stopPropagation from '@/consts/stopPropogation';
import { AriaTypes } from '@/modules/goals/types';
import { Maybe } from '@/types/schema';

import {
  baseButtonFocusStyles,
  TASK_PILL_VARIANT_CONFIG,
  TaskPillVariant,
} from '../TaskStatus/styles';

interface Props extends AriaTypes {
  editable?: boolean;
  date?: Maybe<string>;
  placeholder: string;
  className?: string;
  variant: TaskPillVariant;
  onChange?: (date?: SelectedTime) => void;
  shouldDisableDate?: DatePickerContainerProps['shouldDisableDate'];
  showClearIcon?: boolean;
  disableInteractiveTooltip?: boolean;
}

const TaskDate = (props: Props) => {
  const {
    className,
    variant,
    editable,
    date: dateISO,
    placeholder,
    shouldDisableDate,
    onChange,
    showClearIcon,
    disableInteractiveTooltip = false,
    ...rest
  } = props;
  const date = dateISO ? new Date(dateISO) : undefined;
  const canEdit = editable && onChange;

  const config = TASK_PILL_VARIANT_CONFIG[variant];

  const onDelete = React.useCallback(() => {
    onChange?.(null);
  }, [onChange]);

  const renderContent = () => {
    let label = placeholder;
    let tooltipTitle = placeholder;

    if (date) {
      label = formatDate(date, DateFormat.Date);
      tooltipTitle = formatDate(date, DateFormat.DateTime);
    }

    return (
      <Tooltip
        disableInteractive={disableInteractiveTooltip}
        placement="top"
        title={<Typography variant="body-short-03">{tooltipTitle}</Typography>}>
        <BaseButton
          aria-label="due date"
          className={cn(
            baseButtonFocusStyles,
            'group h-32 w-max py-0 space-s-8',
            config.root.className,
            canEdit ? config.rootEditable.className : 'cursor-default',
          )}
          disabled={!date && !canEdit}
          {...rest}>
          <Calendar2Outline className="h-20 w-20" fillColorToken={config.icon.fillColorToken} />
          <Typography
            className={cn('truncate', config.label.className)}
            color={config.label.color}
            variant="body-short-02">
            {label}
          </Typography>
          {!showClearIcon || !date ? null : (
            <Box onClick={stopPropagation}>
              <LinkButton
                className="h-16 w-16 opacity-0 group-hover:opacity-100"
                color={'internal'}
                onClick={onDelete}>
                <Close className="!h-16 !w-16" fillColorToken={config.icon.fillColorToken} />
              </LinkButton>
            </Box>
          )}
        </BaseButton>
      </Tooltip>
    );
  };

  if (canEdit) {
    return (
      <DatePicker
        pickerProps={{
          variant: DateVariant.Date,
          selectedTime: date,
          onChange,
        }}
        position={config.popoverPosition}
        shouldDisableDate={shouldDisableDate}
        showActionButtons={true}>
        {renderContent()}
      </DatePicker>
    );
  }

  return renderContent();
};

export default TaskDate;
